// ABOUTME: React Query hook for fetching load spacing configuration from the backend API.
// ABOUTME: Calls GET /tuc-configs/load-spacing, finds config for selected scope, and transforms to form-ready shape.

import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'

import {api} from '../../../../App.store'
import {LoadSpacingApiResponse, LoadSpacingConfig, LoadSpacingFormValues, LoadSpacingParameterFormValues, LoadSpacingParameterResponse} from '../types/loadSpacing'

const transformParameter = (param: LoadSpacingParameterResponse): LoadSpacingParameterFormValues => ({
  uomId: param.uom
    ? {isOverridden: param.uom.isOverridden, value: param.uom.value?.id ?? null}
    : null,
  default: param.default,
  min: param.min,
  max: param.max,
  step: param.step,
})

const transformResponse = (response: LoadSpacingApiResponse): LoadSpacingConfig => {
  const formValues: LoadSpacingFormValues = {
    allowStockpiling: response.allowStockpiling,
    timeOnJobSite: response.timeOnJobSite ? transformParameter(response.timeOnJobSite) : null,
    spacing: response.spacing ? transformParameter(response.spacing) : null,
    flowRate: response.flowRate ? transformParameter(response.flowRate) : null,
  }
  return {
    id: response.id,
    ...formValues,
    parentValues: formValues,
    _meta: {
      scopeId: response.scopeId,
      isInherited: false,
      resolvedFrom: null,
      createdAt: response._meta.createdAt,
      updatedAt: response._meta.updatedAt,
      createdBy: response._meta.createdBy,
      updatedBy: response._meta.updatedBy,
    },
  }
}

export const useLoadSpacingConfig = (
  countryId: string | undefined,
  businessLine: string | undefined,
  scopeId: string | undefined,
  rootScopeId?: string | undefined
): {data: LoadSpacingConfig | undefined; isLoading: boolean; error: Error | null} => {
  const normalizedBusinessLine = businessLine?.toUpperCase()
  const {data: rawData, isLoading, error} = useQuery<LoadSpacingApiResponse[], Error>({
    queryKey: ['loadSpacingConfig', countryId, normalizedBusinessLine, scopeId, rootScopeId],
    queryFn: async () => {
      const res = await api.get<LoadSpacingApiResponse[]>('/tuc-configs/load-spacing', {
        params: {
          countryId,
          businessLine: normalizedBusinessLine,
          scopeId,
          rootScopeId,
          resolve: true,
        },
      })
      return res.data ?? []
    },
    enabled: Boolean(countryId && businessLine && scopeId),
    staleTime: 30_000,
  })

  const data = useMemo(() => {
    if (!rawData || !scopeId) return undefined

    const rootRawConfig = rootScopeId
      ? rawData.find((r) => r.scopeId === rootScopeId)
      : undefined

    const rootConfig = rootRawConfig ? transformResponse(rootRawConfig) : undefined
    const parentValues: LoadSpacingFormValues = rootConfig
      ? {allowStockpiling: rootConfig.allowStockpiling, timeOnJobSite: rootConfig.timeOnJobSite, spacing: rootConfig.spacing, flowRate: rootConfig.flowRate}
      : {allowStockpiling: null, timeOnJobSite: null, spacing: null, flowRate: null}

    const match = rawData.find((r) => r.scopeId === scopeId)
    if (match) {
      const config = transformResponse(match)
      return {...config, parentValues}
    }

    if (!rootRawConfig) {
      // No config exists yet — return empty config so the form renders for initial creation
      return {
        id: undefined,
        allowStockpiling: null,
        timeOnJobSite: null,
        spacing: null,
        flowRate: null,
        parentValues,
        _meta: {
          scopeId,
          isInherited: false,
          resolvedFrom: null,
          createdAt: '',
          updatedAt: '',
          createdBy: '',
          updatedBy: '',
        },
      }
    }

    const inherited = transformResponse(rootRawConfig)
    return {
      ...inherited,
      id: undefined,
      parentValues,
      _meta: {...inherited._meta, isInherited: true},
    }
  }, [rawData, scopeId, rootScopeId])

  return {data, isLoading, error}
}
